import { Controller } from "@hotwired/stimulus";

// SMS Form controller for real-time validation, character counting, and cost estimation
export default class extends Controller {
  static targets = [
    "recipient",
    "recipientError",
    "recipientErrorMessage",
    "message",
    "characterCount",
    "characterLimit",
    "charactersRemaining",
    "messageParts",
    "partsPlural",
    "multiPartWarning",
    "warningParts",
    "submitButton",
    "contactSearch",
    "contactResults",
  ];

  static values = {
    estimateUrl: String,
    searchUrl: String,
  };

  connect() {
    this.updateCharacterCount();
    this.validateRecipient();
  }

  // Validate phone number format. PhoneValidator normalizes local numbers
  // server-side, so the client must accept international (+…), local (0…), and
  // bare national formats — only blocking clearly-invalid input (letters, too
  // short). The server (PhoneValidator + model format:) remains authoritative.
  validateRecipient() {
    const recipient = this.recipientTarget.value.trim();
    // Ignore formatting characters (spaces, dashes, parens) before checking.
    const cleaned = recipient.replace(/[\s()\-.]/g, "");
    const phoneRegex = /^\+?\d{7,15}$/;

    if (recipient === "") {
      this.hideRecipientError();
      return true;
    }

    if (!phoneRegex.test(cleaned)) {
      this.showRecipientError(
        "Please enter a valid phone number (e.g., 0772500100 or +256772500100)",
      );
      return false;
    }

    this.hideRecipientError();
    return true;
  }

  // Debounced typeahead against contacts#search (reuses the bulk picker's
  // endpoint + {id,label,phone} JSON shape). KISS: single-select — picking a
  // result fills the recipient field rather than building chips.
  searchContacts() {
    if (!this.hasSearchUrlValue || !this.hasContactResultsTarget) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runContactSearch(), 250);
  }

  async runContactSearch() {
    const term = this.contactSearchTarget.value.trim();
    if (!term) {
      this.contactResultsTarget.innerHTML = "";
      return;
    }
    try {
      const url = `${this.searchUrlValue}?term=${encodeURIComponent(term)}`;
      const matches = await (
        await fetch(url, { headers: { Accept: "application/json" } })
      ).json();
      this.renderContactResults(matches);
    } catch (error) {
      console.error("Failed to search contacts:", error);
    }
  }

  renderContactResults(matches) {
    if (matches.length === 0) {
      this.contactResultsTarget.innerHTML = `<p class="px-3 py-2 text-xs text-gray-400">No matches</p>`;
      return;
    }
    this.contactResultsTarget.innerHTML = matches
      .map(
        (m) =>
          `<button type="button" data-action="sms-form#pickContact" data-phone="${m.phone}"
             class="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
             ${this.escapeHtml(m.label)} <span class="text-gray-400">${m.phone}</span>
           </button>`,
      )
      .join("");
  }

  // Fill the recipient from a chosen contact and re-run validation + estimate.
  pickContact(event) {
    this.recipientTarget.value = event.currentTarget.dataset.phone;
    this.contactSearchTarget.value = "";
    this.contactResultsTarget.innerHTML = "";
    this.validateRecipient();
    this.updateCostEstimate();
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Update character count and SMS parts calculation
  updateCharacterCount() {
    const message = this.messageTarget.value;
    const characterCount = message.length;

    // Calculate SMS parts matching server-side logic
    const isGSM7Bit = this.isGSM7Bit(message);

    let messageParts;
    if (characterCount === 0) {
      messageParts = 1;
    } else if (isGSM7Bit) {
      // GSM 7-bit encoding
      if (characterCount <= 160) {
        messageParts = 1;
      } else {
        // Concatenated SMS: 153 chars per part after first part
        messageParts = Math.ceil((characterCount - 160) / 153) + 1;
      }
    } else {
      // Unicode encoding (UCS-2)
      if (characterCount <= 70) {
        messageParts = 1;
      } else {
        // Concatenated SMS: 67 chars per part after first part
        messageParts = Math.ceil((characterCount - 70) / 67) + 1;
      }
    }

    // Calculate character limits based on encoding and parts
    let currentLimit, charactersRemaining;
    if (isGSM7Bit) {
      if (messageParts === 1) {
        currentLimit = 160;
      } else {
        currentLimit = 160 + (messageParts - 1) * 153;
      }
    } else {
      if (messageParts === 1) {
        currentLimit = 70;
      } else {
        currentLimit = 70 + (messageParts - 1) * 67;
      }
    }
    charactersRemaining = currentLimit - characterCount;

    // Update character count display
    this.characterCountTarget.textContent = characterCount;
    this.characterLimitTarget.textContent = currentLimit;
    this.charactersRemainingTarget.textContent = charactersRemaining;
    this.messagePartsTarget.textContent = messageParts;

    // Show/hide plural form for parts
    if (messageParts > 1) {
      this.partsPluralTarget.classList.remove("hidden");
    } else {
      this.partsPluralTarget.classList.add("hidden");
    }

    // Show/hide multi-part warning
    if (messageParts > 1) {
      this.warningPartsTarget.textContent = messageParts;
      this.multiPartWarningTarget.classList.remove("hidden");
    } else {
      this.multiPartWarningTarget.classList.add("hidden");
    }

    // Update color based on remaining characters
    const remainingDisplay = this.charactersRemainingTarget;
    if (charactersRemaining < 0) {
      remainingDisplay.classList.add("text-red-600", "dark:text-red-400");
      remainingDisplay.classList.remove(
        "text-gray-500",
        "dark:text-gray-400",
        "text-amber-600",
        "dark:text-amber-400",
      );
    } else if (charactersRemaining < 20) {
      remainingDisplay.classList.add("text-amber-600", "dark:text-amber-400");
      remainingDisplay.classList.remove(
        "text-gray-500",
        "dark:text-gray-400",
        "text-red-600",
        "dark:text-red-400",
      );
    } else {
      remainingDisplay.classList.add("text-gray-500", "dark:text-gray-400");
      remainingDisplay.classList.remove(
        "text-red-600",
        "dark:text-red-400",
        "text-amber-600",
        "dark:text-amber-400",
      );
    }
  }

  // Update cost estimate via AJAX
  async updateCostEstimate() {
    if (!this.hasEstimateUrlValue) return;

    const recipient = this.recipientTarget.value.trim();
    const message = this.messageTarget.value;
    const gateway =
      this.element.querySelector('[name="sms_message[gateway_provider]"]')
        ?.value || "africastalking";

    // Only estimate if we have a message
    if (message === "") return;

    try {
      const response = await fetch(this.estimateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify({
          message: message,
          gateway_provider: gateway,
          recipient: recipient,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.updateCostDisplay(data);
      }
    } catch (error) {
      console.error("Failed to estimate cost:", error);
    }
  }

  // Update the cost display elements
  updateCostDisplay(data) {
    // Update cost preview in sidebar
    const costAmount = document.querySelector("[data-cost-amount]");
    const messageParts = document.querySelector("[data-message-parts]");
    const characterCount = document.querySelector("[data-character-count]");

    if (costAmount) costAmount.textContent = data.formatted_cost;
    if (messageParts) messageParts.textContent = data.message_parts;
    if (characterCount) characterCount.textContent = data.characters_used;
  }

  // Check if text contains only GSM 7-bit characters (simplified check)
  isGSM7Bit(text) {
    // Simple check: if it's ASCII and doesn't contain special Unicode chars, treat as GSM 7-bit
    // This matches the server-side logic: ascii_only? && !match?(/[{}\\~\[\]|€]/)
    return /^[\x00-\x7F]*$/.test(text) && !/[{}\\~\[\]|€]/.test(text);
  }

  // Show recipient validation error
  showRecipientError(message) {
    this.recipientErrorMessageTarget.textContent = message;
    this.recipientErrorTarget.classList.remove("hidden");
    this.recipientTarget.classList.add(
      "border-red-500",
      "focus:border-red-500",
      "focus:ring-red-500",
    );
    this.recipientTarget.classList.remove(
      "border-gray-300",
      "focus:border-primary-500",
      "focus:ring-primary-500",
    );
  }

  // Hide recipient validation error
  hideRecipientError() {
    this.recipientErrorTarget.classList.add("hidden");
    this.recipientTarget.classList.remove(
      "border-red-500",
      "focus:border-red-500",
      "focus:ring-red-500",
    );
    this.recipientTarget.classList.add(
      "border-gray-300",
      "focus:border-primary-500",
      "focus:ring-primary-500",
    );
  }

  // Handle form submission
  beforeSubmit(event) {
    const isValidRecipient = this.validateRecipient();
    const hasMessage = this.messageTarget.value.trim() !== "";

    if (!isValidRecipient || !hasMessage) {
      event.preventDefault();

      if (!hasMessage) {
        // You could add a message validation error here
        this.messageTarget.focus();
      }

      return false;
    }

    // Disable submit button to prevent double submission
    this.submitButtonTarget.disabled = true;
    return true;
  }
}
